import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ChevronRight, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';

// Windows in minutes.
const OPEN_WINDOW_MIN = 60;   // countdown ticker appears at T-60m
const CHECKIN_WINDOW_MIN = 30; // "Check in" CTA appears at T-30m

/**
 * VirtualCountdownCard — inline widget shown on EventDetail for a JOINED
 * participant of a VIRTUAL community event, controlling their pre-race
 * experience.
 *
 * State machine (drivers: event.date, event.raceState):
 *   • T > 60m               → static "You're in" pill (delegate to caller)
 *   • 30m < T ≤ 60m         → live mm:ss countdown, label "Countdown to check-in"
 *   • 0    < T ≤ 30m        → live mm:ss countdown + primary "Check in" CTA
 *                             linking to /events/:id/checkin
 *   • T ≤ 0, race NOT live  → "Waiting for race to start" waiting state
 *                             (parent polls event so raceState flips to live)
 *
 * When raceState becomes 'live' the parent's existing auto-redirect
 * effect kicks in and takes the runner to /events/:id/race, so this
 * component only needs to render the waiting UI in that gap.
 *
 * Returns null when `active` is false so the caller can fall back to
 * whatever it normally renders (static "You're in").
 */
export function VirtualCountdownCard({
  event,
  myParticipation,
  active,
  onWaitingTick,
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [active]);

  const eventMs = useMemo(() => (event?.date ? new Date(event.date).getTime() : null), [event?.date]);
  const diffMs = eventMs != null ? eventMs - nowMs : null;
  const diffMin = diffMs != null ? diffMs / 60000 : null;

  // Poll the parent while we're inside the waiting state so raceState flips
  // over to 'live' without a manual refresh.
  const waiting = diffMs != null && diffMs <= 0 && event?.raceState !== 'live';
  useEffect(() => {
    if (!active || !waiting || !onWaitingTick) return;
    const iv = setInterval(() => onWaitingTick(), 5000);
    return () => clearInterval(iv);
  }, [active, waiting, onWaitingTick]);

  if (!active || diffMs == null || diffMin > OPEN_WINDOW_MIN) {
    // Outside the countdown window — render the plain "You're in" pill so
    // the caller can drop this component in unconditionally for virtual
    // events without having to keep its own fallback.
    const bibFar = event?.bibMode && event.bibMode !== 'none' && myParticipation?.bibNumber
      ? ` · Bib ${myParticipation.bibNumber}`
      : '';
    return (
      <div
        className="col-span-2 h-11 rounded-md border border-success bg-success/10 text-success flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-widest"
        data-testid="event-already-joined"
      >
        <CheckCircle2 className="h-4 w-4" />
        You&apos;re in{bibFar}
      </div>
    );
  }

  const totalSec = Math.max(0, Math.floor(diffMs / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  const countdownLabel = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;

  const bib = event.bibMode !== 'none' && myParticipation?.bibNumber
    ? `Bib ${myParticipation.bibNumber}`
    : null;

  // Waiting state — event.date has passed, organizer hasn't fired Start.
  if (waiting) {
    return (
      <div
        className="col-span-2 rivl-card p-4 flex items-center gap-3"
        data-testid="virtual-waiting"
      >
        <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="rivl-label text-[10px]">Waiting for race to start</div>
          <p className="text-sm text-foreground mt-0.5 font-medium leading-tight">
            The organizer hasn&apos;t started this race yet. You&apos;ll be sent to the live screen the moment they do.
          </p>
        </div>
      </div>
    );
  }

  // Check-in window — 0 < T ≤ 30m.
  const inCheckinWindow = diffMin <= CHECKIN_WINDOW_MIN;

  return (
    <div
      className="col-span-2 rivl-card p-4"
      data-testid={inCheckinWindow ? 'virtual-checkin-cta' : 'virtual-countdown'}
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          {inCheckinWindow ? <CheckCircle2 className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="rivl-label text-[10px]">
            {inCheckinWindow ? 'Check-in open' : 'Countdown to check-in'}
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span
              className="font-mono font-bold text-2xl tabular-nums text-foreground"
              data-testid="virtual-countdown-clock"
            >
              {countdownLabel}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {inCheckinWindow ? 'until race' : 'until check-in opens'}
            </span>
          </div>
          {bib && (
            <div className="text-[11px] text-muted-foreground mt-1 font-mono">
              You&apos;re in · {bib}
            </div>
          )}
        </div>
      </div>
      {inCheckinWindow && (
        <Button
          asChild
          className="w-full h-11 rounded-md mt-3 text-sm font-semibold"
          data-testid="virtual-checkin-btn"
        >
          <Link to={`/events/${event.id}/checkin`}>
            Check in <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      )}
    </div>
  );
}
